<template>
  <div>
    <form @submit.prevent="onSubmit">
      <Message
        :closable="false"
        severity="secondary"
        :pt="{
          icon: {
            class: 'mt-1 text-xl align-self-start',
          },
          text: {
            class: 'font-normal',
          },
        }"
        icon="pi pi-info-circle"
      >
        You must have a payment method on file before your hosts can receive your campaign.
        You may either add a card now or download a
         <Button
          class="px-1 py-0"
          label="Credit Application Form"
          icon="pi pi-download"
          iconPos="right"
          text
          @click="onCreditAppDownload"
         />.
        Once complete, please email the filled out form to
          <strong>{{ FINANCE_EMAIL }}</strong>
        <br /><br />

        You will be charged based on the amount of impressions your campaign has received.
        This charge occurs at the end of each month for any running campaigns.

        <p>
          Need help or have any questions? Contact
          <a
            class="text-primary"
            :href="mailTo"
          >
            {{ SUPPORT_EMAIL }}
          </a>
        </p>
      </Message>
      <div
        ref="paymentElement"
        id='payment-element'
      />
      <div class="flex justify-content-end mt-3">
        <Button
          v-if="allowSkip"
          text
          plain
          label="Skip"
          @click="onClickSkip"
        />
        <Button
          v-if="paymentElement"
          class="ml-2"
          label="Add Payment Method"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { mapStores } from 'pinia';
import { useMyUserStore } from '@/stores';
import * as api from '@/api';
import { parseMessageFromError } from '@/utils/errors';
import { FINANCE_EMAIL, SUPPORT_EMAIL } from '@/constants';
import { generateBodyText } from '@/utils/support';
import { downloadFile } from '@/utils/helpers';

const PUBLISHABLE_KEY = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;

export default {
  props: {
    redirectUrl: String,
    allowSkip: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapStores(useMyUserStore),
    mailTo() {
      const subject = 'Ekoz.ai Payment Question';
      const body = generateBodyText({
        userId: this.myUserStore.userId,
        url: window.location.href,
      });

      return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    },
    creditAppFormMailTo() {
      const subject = 'Ekoz.ai Credit App Form';
      const body = generateBodyText({
        userId: this.myUserStore.userId,
        url: window.location.href,
      });

      return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      paymentElement: null,
      SUPPORT_EMAIL,
      FINANCE_EMAIL,
    };
  },
  mounted() {
    this.loadPaymentMethodForm();
  },
  methods: {
    async loadPaymentMethodForm() {
      try {
        const res = await api.createSetupIntent();

        this.stripe = await loadStripe(PUBLISHABLE_KEY);

        this.elements = this.stripe.elements({ clientSecret: res.client_secret });

        const options = {
          layout: {
            type: 'accordion',
          },
        };

        this.paymentElement = this.elements.create('payment', options);

        if (this.$refs.paymentElement) {
          this.paymentElement.mount('#payment-element');
        }
      } catch (error) {
        const message = parseMessageFromError(error, 'Error loading payment method form.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    async onSubmit() {
      const { error } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: this.redirectUrl,
        },
      });

      if (error && error.message) {
        this.$toast.add({
          severity: 'error',
          detail: error.message,
        });
      } else if (error) {
        this.$toast.add({
          severity: 'error',
          detail: 'Error confirming payment method.',
        });
      }
    },
    async onCreditAppDownload() {
      downloadFile('/forms/credit_app_v1_2.docx', 'ekoz_ai_credit_application_v1_2.docx');
    },
    onClickSkip() {
      this.$emit('cancel', true);
    },
  },
};
</script>
